import './fabricDefaults.ts';
import {
    Canvas,
    Group,
    IText,
    Rect,
    Shadow,
    Textbox,
    classRegistry,
    util
} from 'fabric';
import humanFileSize from '../../lib/humanFileSize.ts';
import {
    ActionGlyphs,
    DefaultFileGlyph,
    GlyphSpec,
    LinkGlyph,
    glyphSpecForExtension,
    makeGlyph
} from './resourceCardIcons.ts';

export type ResourceCardKind = 'link' | 'file';

export const ResourceCardColors = [
    '#FFFFFF', // white (default)
    '#FFF9B1', // yellow
    '#D5F692', // light green
    '#FFCEE0', // pink
    '#A6CCF5', // blue
    '#BE9BF1' // purple
];

const DEFAULT_WIDTH = 300;
// The same minimum the agent tools enforce (WhiteboardBuilder.CardMinimumWidth).
const MIN_WIDTH = 160;
// Below this the action badge goes, so the title keeps a readable column.
const COMPACT_WIDTH = 220;
const MIN_HEIGHT = 88;
const PADDING = 14;
const TILE_SIZE = 44;
const BADGE_SIZE = 28;
const GAP = 14;
const TITLE_FONT_SIZE = 14;
const SUBTITLE_FONT_SIZE = 11;
const TITLE_GAP = 4;
const TITLE_COLOR = '#1F1F1F';
const SUBTITLE_COLOR = '#6B6B6B';
const BORDER_COLOR = '#D9D9D9';
const SEPARATOR = '  ·  ';

export const ResourceCardDefaultFontFamily = 'Poppins, sans-serif';

export interface ResourceCardOptions {
    left?: number;
    top?: number;
    width?: number;
    scaleX?: number;
    scaleY?: number;
    angle?: number;
    flipX?: boolean;
    flipY?: boolean;
    kind?: ResourceCardKind;
    url?: string;
    title?: string;
    fileName?: string;
    extension?: string;
    byteLength?: number | null;
    fill?: string;
}

export interface ResourceCardTarget {
    kind: ResourceCardKind;
    url: string;
    fileName?: string;
    extension?: string;
    byteLength?: number | null;
    title?: string;
}

/*
 * A clickable resource on the whiteboard: either an external web link or a
 * file from the author's assets. Built as a v7 group of a rounded body, a
 * coloured icon tile, a title textbox, a derived subtitle and a circular
 * action badge, so it drags, resizes and persists as one element.
 *
 * Serialized as a compact flat option bag rather than as a generic group with
 * children (see toObject): roughly a tenth of the JSON, and the title stays
 * editable because the restore path rebuilds the card from its options.
 *
 * NOTE ON nav: the card sets nav to its own url. That property is what the
 * canvas already uses to decide which objects stay clickable in read-only and
 * pan modes (onIsReadOnlyChange and setMovingCanvas in CanvasController,
 * onRestoreImpl in lib.ts), so setting it lights up all of that plumbing with
 * no changes there. It must NEVER reach CanvasController.navigate, which is
 * react-router's and would treat an absolute url as an in-app path.
 * handleMouseDown therefore tests instanceof ResourceCard and returns before
 * it gets to the nav branch.
 */
export class ResourceCard extends Group {
    static type = 'resource-card';

    body: Rect;
    tile: Rect;
    titleText: Textbox;
    subtitleText: IText;
    badge: Rect;

    nav: string | null = null;

    private cardKind: ResourceCardKind;
    private cardUrl: string;
    private cardFileName: string;
    private cardExtension: string;
    private cardByteLength: number | null;
    private baseWidth: number;

    constructor(options: ResourceCardOptions = {}) {
        const {
            kind = 'link',
            url = '',
            title = '',
            fileName = '',
            extension = '',
            byteLength = null,
            fill = ResourceCardColors[0],
            width = DEFAULT_WIDTH,
            ...groupOptions
        } = options;

        const spec = ResourceCard.glyphSpec(kind, extension);

        const body = new Rect({
            left: -width / 2,
            top: -MIN_HEIGHT / 2,
            width,
            height: MIN_HEIGHT,
            rx: 10,
            ry: 10,
            fill,
            stroke: BORDER_COLOR,
            strokeWidth: 1,
            selectable: false,
            evented: false,
            shadow: new Shadow({
                color: 'rgba(0, 0, 0, 0.18)',
                blur: 8,
                offsetX: 0,
                offsetY: 3
            })
        });

        const tile = new Rect({
            left: -width / 2 + PADDING,
            top: -TILE_SIZE / 2,
            width: TILE_SIZE,
            height: TILE_SIZE,
            rx: 8,
            ry: 8,
            fill: spec.accent,
            selectable: false,
            evented: false
        });

        const badge = new Rect({
            left: width / 2 - PADDING - BADGE_SIZE,
            top: -BADGE_SIZE / 2,
            width: BADGE_SIZE,
            height: BADGE_SIZE,
            rx: BADGE_SIZE / 2,
            ry: BADGE_SIZE / 2,
            fill: spec.accent,
            selectable: false,
            evented: false
        });

        const titleText = new Textbox(
            title || ResourceCard.fallbackTitle(kind, fileName, url),
            {
                left: -width / 2 + PADDING + TILE_SIZE + GAP,
                top: -TITLE_FONT_SIZE,
                width: ResourceCard.textColumnWidth(width),
                fontSize: TITLE_FONT_SIZE,
                fontFamily: ResourceCardDefaultFontFamily,
                fontWeight: 'bold',
                fill: TITLE_COLOR,
                /*
                 * Editable in place, but never transformable on its own: it
                 * stays the active object right after an edit, and without
                 * these locks its handles would move the text out of sync
                 * with the card body.
                 */
                hasControls: false,
                lockMovementX: true,
                lockMovementY: true,
                lockRotation: true,
                lockScalingX: true,
                lockScalingY: true
            }
        );

        const subtitleText = new IText('', {
            left: -width / 2 + PADDING + TILE_SIZE + GAP,
            top: TITLE_GAP,
            fontSize: SUBTITLE_FONT_SIZE,
            fontFamily: ResourceCardDefaultFontFamily,
            fill: SUBTITLE_COLOR,
            selectable: false,
            evented: false
        });

        super([body, tile, badge, titleText, subtitleText], {
            subTargetCheck: true,
            interactive: false,
            lockScalingFlip: true
        });

        this.body = body;
        this.tile = tile;
        this.badge = badge;
        this.titleText = titleText;
        this.subtitleText = subtitleText;

        this.cardKind = kind;
        this.cardUrl = url;
        this.cardFileName = fileName;
        this.cardExtension = extension;
        this.cardByteLength = byteLength;
        this.baseWidth = width;

        this.nav = url || null;

        // Always reads as clickable, in both edit and read-only modes.
        this.hoverCursor = 'pointer';

        this.on('modified', this.bakeScale);
        this.titleText.on('changed', () => this.layoutContent());

        this.refreshSubtitle();
        this.layoutContent();

        // Geometry last: the group's own layout has sized it from the
        // children by now, so left/top/scale/angle position the finished card.
        this.set(groupOptions);
        this.setCoords();
    }

    // Whether a card this wide shows the action badge; a narrower one gives the title the room instead.
    private static showsBadge(width: number): boolean {
        return width >= COMPACT_WIDTH;
    }

    private static textColumnWidth(width: number): number {
        const badgeRoom = ResourceCard.showsBadge(width) ? GAP + BADGE_SIZE : 0;
        return Math.max(width - PADDING * 2 - TILE_SIZE - GAP - badgeRoom, 40);
    }

    private static glyphSpec(
        kind: ResourceCardKind,
        extension: string
    ): GlyphSpec {
        if (kind === 'link') {
            return LinkGlyph;
        }
        return extension ? glyphSpecForExtension(extension) : DefaultFileGlyph;
    }

    /*
     * A card always shows something readable even when the author gives it no
     * title: the file's own name, or the link's hostname.
     */
    private static fallbackTitle(
        kind: ResourceCardKind,
        fileName: string,
        url: string
    ): string {
        if (kind === 'file') {
            const name = (fileName || url).split(/[/\\]/).pop() ?? '';
            try {
                return decodeURIComponent(name) || 'File';
            } catch {
                return name || 'File';
            }
        }
        return ResourceCard.hostnameOf(url) || 'Link';
    }

    private static hostnameOf(url: string): string {
        try {
            return new URL(url).hostname;
        } catch {
            return '';
        }
    }

    private glyphs(): Group['_objects'] {
        return this.getObjects().filter(
            object =>
                object !== this.body &&
                object !== this.tile &&
                object !== this.badge &&
                object !== this.titleText &&
                object !== this.subtitleText
        );
    }

    /*
     * Rebuilds the two glyphs. They are recreated rather than mutated because
     * the path data itself changes whenever the kind or the file type changes,
     * and they are cheap: two synchronous fabric Groups, no network.
     */
    private refreshGlyphs() {
        const spec = ResourceCard.glyphSpec(this.cardKind, this.cardExtension);

        this.tile.set('fill', spec.accent);
        this.badge.set('fill', spec.accent);

        this.glyphs().forEach(object => this.remove(object));

        const tileGlyph = makeGlyph(
            spec.icon,
            22,
            '#FFFFFF',
            this.tile.left + TILE_SIZE / 2,
            0
        );
        const badgeGlyph = makeGlyph(
            this.cardKind === 'file'
                ? ActionGlyphs.download
                : ActionGlyphs.open,
            13,
            '#FFFFFF',
            this.badge.left + BADGE_SIZE / 2,
            0
        );

        /*
         * The glyphs are placed in the card's own plane, but Group.add reads
         * an added object's position as a board position and moves it into
         * the card. On a card that is on the board (changed by someone else,
         * say) that put the icons far outside it and stretched the card to
         * reach them. Taking them out to the board first makes the round trip
         * land them where they belong.
         */
        const cardTransform = this.calcTransformMatrix();
        [tileGlyph, badgeGlyph].forEach(glyph =>
            util.addTransformToObject(glyph, cardTransform)
        );

        badgeGlyph.visible = this.badge.visible;
        this.add(tileGlyph, badgeGlyph);
    }

    /*
     * The subtitle is always derived, never stored: it names the action in
     * words next to the badge icon, then says what the resource is. Showing a
     * link's hostname lets a viewer see where a card goes before clicking it.
     */
    private refreshSubtitle() {
        if (this.cardKind === 'file') {
            const parts = ['Download'];
            if (this.cardExtension) {
                parts.push(this.cardExtension.toUpperCase());
            }
            const size = humanFileSize(this.cardByteLength);
            if (size) {
                parts.push(size);
            }
            this.subtitleText.set('text', parts.join(SEPARATOR));
            return;
        }

        const hostname = ResourceCard.hostnameOf(this.cardUrl);
        this.subtitleText.set(
            'text',
            hostname ? 'Open' + SEPARATOR + hostname : 'Open link'
        );
    }

    /*
     * Positions everything from the current base width and stacks the title
     * and subtitle as one vertically centred block, growing the body when the
     * title wraps to more lines. The group's own position is pinned across the
     * re-layout, otherwise fit-content layout drifts the card on every pass.
     */
    private layoutContent() {
        const {left, top} = this;
        const width = this.baseWidth;
        const textLeft = -width / 2 + PADDING + TILE_SIZE + GAP;

        const columnWidth = ResourceCard.textColumnWidth(width);

        /*
         * Fabric clamps a textbox's width up to its longest word and never
         * back down, so a title with no spaces in it (a file name, typically)
         * would render straight over the action badge. Word wrapping is tried
         * first because it reads better, and only a title that still does not
         * fit is allowed to break inside a word.
         */
        this.titleText.set({splitByGrapheme: false, width: columnWidth});
        if (this.titleText.width > columnWidth + 0.5) {
            this.titleText.set({splitByGrapheme: true, width: columnWidth});
        }

        const blockHeight =
            this.titleText.height + TITLE_GAP + this.subtitleText.height;
        const height = Math.max(MIN_HEIGHT, blockHeight + PADDING * 2);

        this.body.set({
            left: -width / 2,
            top: -height / 2,
            width,
            height
        });
        this.tile.set({left: -width / 2 + PADDING, top: -TILE_SIZE / 2});
        this.badge.set({
            left: width / 2 - PADDING - BADGE_SIZE,
            top: -BADGE_SIZE / 2,
            visible: ResourceCard.showsBadge(width)
        });
        this.titleText.set({left: textLeft, top: -blockHeight / 2});
        this.subtitleText.set({
            left: textLeft,
            top: -blockHeight / 2 + this.titleText.height + TITLE_GAP
        });

        // The glyphs are placed from the tile and badge, so they follow.
        this.refreshGlyphs();

        this.triggerLayout();
        this.set({left, top});
        this.setCoords();
        this.dirty = true;
    }

    get kind(): ResourceCardKind {
        return this.cardKind;
    }

    get url(): string {
        return this.cardUrl;
    }

    get title(): string {
        return this.titleText.text ?? '';
    }

    get color(): string {
        return this.body.fill as string;
    }

    setTitle(title: string) {
        this.titleText.set('text', title);
        this.layoutContent();
    }

    setColor(color: string) {
        this.body.set('fill', color);
        this.dirty = true;
    }

    /*
     * Repoints the card at a different resource, updating the glyph, the
     * accent colour, the derived subtitle and nav together. The title is only
     * replaced when it was still the auto-generated fallback, so an author's
     * own wording survives a change of target.
     */
    setTarget(target: ResourceCardTarget) {
        const previousFallback = ResourceCard.fallbackTitle(
            this.cardKind,
            this.cardFileName,
            this.cardUrl
        );
        const authoredTitle =
            this.title && this.title !== previousFallback ? this.title : '';

        this.cardKind = target.kind;
        this.cardUrl = target.url;
        this.cardFileName = target.fileName ?? '';
        this.cardExtension = target.extension ?? '';
        this.cardByteLength = target.byteLength ?? null;
        this.nav = target.url || null;

        const nextTitle =
            target.title ||
            authoredTitle ||
            ResourceCard.fallbackTitle(
                this.cardKind,
                this.cardFileName,
                this.cardUrl
            );

        this.titleText.set('text', nextTitle);
        this.refreshSubtitle();
        this.layoutContent();
    }

    /*
     * Native in-place editing of the title. The group is interactive only for
     * the duration of the edit so mouse events reach the child; the rest of
     * the time the card is targeted and moved as one unit.
     */
    enterTitleEditing(canvas: Canvas) {
        this.interactive = true;
        const titleText = this.titleText;

        canvas.setActiveObject(titleText);
        titleText.enterEditing();
        titleText.setSelectionStart(0);
        titleText.setSelectionEnd(titleText.text.length);

        titleText.once('editing:exited', () => {
            this.interactive = false;
            this.layoutContent();

            /*
             * If the title is still the selection (edit ended via Escape or a
             * click on the card itself), hand the selection back to the card
             * so later gestures transform the whole thing.
             */
            if (canvas.getActiveObject() === titleText) {
                canvas.setActiveObject(this);
            }

            canvas.requestRenderAll();
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            canvas.fire('object:modified' as any, {target: this});
        });

        canvas.requestRenderAll();
    }

    /*
     * Applies persisted element properties (initial restore diffs and remote
     * multiplayer merges). Target and child-owned properties are routed
     * through the setters; everything else is group geometry.
     */
    applyElementProps(properties: Record<string, unknown>) {
        const {
            kind,
            url,
            title,
            fileName,
            extension,
            byteLength,
            fill,
            width,
            height,
            ...groupProperties
        } = properties;

        void height; // derived from the content, never applied directly

        if (typeof width === 'number') {
            this.baseWidth = Math.max(MIN_WIDTH, width);
        }

        const targetChanged =
            typeof kind === 'string' ||
            typeof url === 'string' ||
            typeof fileName === 'string' ||
            typeof extension === 'string' ||
            byteLength !== undefined;

        if (targetChanged) {
            this.setTarget({
                kind: (typeof kind === 'string'
                    ? kind
                    : this.cardKind) as ResourceCardKind,
                url: typeof url === 'string' ? url : this.cardUrl,
                fileName:
                    typeof fileName === 'string' ? fileName : this.cardFileName,
                extension:
                    typeof extension === 'string'
                        ? extension
                        : this.cardExtension,
                byteLength:
                    typeof byteLength === 'number' || byteLength === null
                        ? (byteLength as number | null)
                        : this.cardByteLength,
                title: typeof title === 'string' ? title : this.title
            });
        } else if (typeof title === 'string' && title !== this.title) {
            this.titleText.set('text', title);
        }

        if (typeof fill === 'string') {
            this.setColor(fill);
        }

        this.set(groupProperties);
        this.layoutContent();
        this.dirty = true;
        this.setCoords();
    }

    /*
     * A resize gesture changes scaleX/scaleY; convert that into a real width
     * once the gesture ends so the text keeps its font size and the title
     * rewraps into the new area instead of being zoomed. Height follows the
     * content, so vertical scale is simply discarded.
     */
    private bakeScale = () => {
        if (this.scaleX === 1 && this.scaleY === 1) {
            return;
        }

        const width = Math.max(MIN_WIDTH, this.body.width * this.scaleX);

        this.set({scaleX: 1, scaleY: 1});
        this.baseWidth = width;
        this.layoutContent();

        if (this.canvas) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            this.canvas.fire('object:modified' as any, {target: this});
            this.canvas.requestRenderAll();
        }
    };

    /*
     * Serialized as a compact custom element instead of a generic group with
     * children: an order of magnitude less data, and the restore path simply
     * reconstructs the card from these options. There must never be an
     * objects array in here.
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    toObject(): any {
        return {
            type: 'resource-card',
            left: this.left,
            top: this.top,
            width: this.baseWidth,
            scaleX: this.scaleX,
            scaleY: this.scaleY,
            angle: this.angle,
            flipX: this.flipX,
            flipY: this.flipY,
            kind: this.cardKind,
            url: this.cardUrl,
            title: this.title,
            fileName: this.cardFileName,
            extension: this.cardExtension,
            byteLength: this.cardByteLength,
            fill: this.color
        };
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    static fromObject(object: any): Promise<ResourceCard> {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const {type, version, ...options} = object ?? {};
        return Promise.resolve(
            new ResourceCard(options as ResourceCardOptions)
        );
    }
}

classRegistry.setClass(ResourceCard);
